import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import AlertModal from "_shared/components/Modal/AlertModal/AlertModal";
import ConfirmationModal from "_shared/components/Modal/ConfirmationModal/ConfirmationModal";

const ModalContext = createContext({
  onErrorMsg: (_msg: ReactNode, _onConfirm?: () => void) => {},
  onSuccessMsg: (_msg: ReactNode, _onConfirm?: () => void) => {},
  awaitConfirmation<T>(_onConfirm: () => Promise<T>, _msg: ReactNode) {
    return new Promise<T>(() => {});
  },
});

export const ModalProvider: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const [confirmationModal, setConfirmationModal] = useState<{
    message: ReactNode;
    awaitConfirmation: () => Promise<void>;
  } | null>(null);

  const [alertModal, setAlertModal] = useState<{
    type: "success" | "error";
    onConfirm?: () => void;
    message: ReactNode;
  } | null>(null);

  const handleErrorMsg = useCallback(
    (msg: ReactNode, onConfirm?: () => void) => {
      setAlertModal({
        type: "error",
        onConfirm,
        message: msg,
      });
    },
    [],
  );

  const handleSuccessMsg = useCallback(
    (msg: ReactNode, onConfirm?: () => void) => {
      setAlertModal({
        type: "success",
        onConfirm,
        message: msg,
      });
    },
    [],
  );

  const awaitConfirmation = useCallback(async function <T>(
    onConfirm: () => Promise<T>,
    msg: ReactNode,
  ) {
    return new Promise<T>((resolve, reject) => {
      setConfirmationModal({
        message: msg,
        awaitConfirmation: async () => {
          try {
            const res = await onConfirm();
            resolve(res);
          } catch (err) {
            reject(err);
          }
        },
      });
    });
  },
  []);

  return (
    <ModalContext.Provider
      value={{
        onErrorMsg: handleErrorMsg,
        onSuccessMsg: handleSuccessMsg,
        awaitConfirmation,
      }}
    >
      {children}
      {/* confirmation modals at the back */}
      {confirmationModal && (
        <ConfirmationModal
          open={!!confirmationModal}
          onConfirm={confirmationModal.awaitConfirmation}
          onClose={() => setConfirmationModal(null)}
        >
          {confirmationModal.message}
        </ConfirmationModal>
      )}
      {/* alert modals at the forefront */}
      {alertModal && (
        <AlertModal
          open={!!alertModal}
          onClose={() => {
            setAlertModal(null);
            if (alertModal.onConfirm) alertModal.onConfirm();
          }}
          type={alertModal.type}
          message={alertModal.message as string}
        />
      )}
    </ModalContext.Provider>
  );
};

export const useModal = () => useContext(ModalContext);
